// Best-effort device/browser diagnostics for the log file header and the
// session-start log entry. Every field is optional — browsers expose different
// subsets — and collection NEVER throws (logging must not break product code).

export interface JsHeap {
  usedMb: number;
  totalMb: number;
  limitMb: number;
}

export interface SystemInfo {
  browser?: string; // e.g. "Chrome 124"
  engine?: string; // e.g. "Blink" | "Gecko" | "WebKit"
  os?: string; // e.g. "Windows 11" | "macOS 14.4" | "Android 14"
  architecture?: string; // e.g. "x86" | "arm" (+ bitness when known)
  deviceModel?: string; // mobile device model when exposed
  mobile?: boolean;
  cpuCores?: number; // navigator.hardwareConcurrency
  deviceMemoryGb?: number; // navigator.deviceMemory (coarse, Chromium-only)
  jsHeap?: JsHeap; // performance.memory (Chromium-only)
  screen?: string; // "1920x1080 @2x"
  viewport?: string; // "1280x720"
  language?: string;
  languages?: string;
  timezone?: string;
  network?: string; // "4g ~10.0Mbps rtt 50ms saveData"
  online?: boolean;
  cookiesEnabled?: boolean;
  touchPoints?: number;
  userAgent?: string;
}

// Minimal shapes for the not-yet-ubiquitous APIs we read defensively.
interface UADataBrand { brand: string; version: string; }
interface UADataLowEntropy {
  brands?: UADataBrand[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
}
interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

// Coarse OS guess from a UA string when navigator.userAgentData is unavailable.
function osFromUserAgent(ua: string): string | undefined {
  if (/Windows NT 10/.test(ua)) return 'Windows 10/11';
  if (/Windows/.test(ua)) return 'Windows';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/CrOS/.test(ua)) return 'ChromeOS';
  if (/Linux/.test(ua)) return 'Linux';
  return undefined;
}

// Coarse browser guess from a UA string (fallback path only).
function browserFromUserAgent(ua: string): string | undefined {
  const edge = /Edg\/([\d.]+)/.exec(ua);
  if (edge) return `Edge ${edge[1]}`;
  const chrome = /Chrome\/([\d.]+)/.exec(ua);
  if (chrome && !/OPR|Edg/.test(ua)) return `Chrome ${chrome[1]}`;
  const firefox = /Firefox\/([\d.]+)/.exec(ua);
  if (firefox) return `Firefox ${firefox[1]}`;
  const safari = /Version\/([\d.]+).*Safari/.exec(ua);
  if (safari) return `Safari ${safari[1]}`;
  return undefined;
}

function engineFromUserAgent(ua: string): string | undefined {
  if (/Gecko\/\d/.test(ua)) return 'Gecko';
  if (/AppleWebKit/.test(ua)) return /Chrome\//.test(ua) ? 'Blink' : 'WebKit';
  return undefined;
}

export async function collectSystemInfo(): Promise<SystemInfo> {
  const info: SystemInfo = {};

  const nav: Navigator | undefined =
    typeof navigator !== 'undefined' ? navigator : undefined;
  const ua = nav?.userAgent ?? '';
  info.userAgent = ua || undefined;

  // --- Browser / OS via UA-Client-Hints, falling back to the UA string. ---
  const uaData = safe(() => (nav as unknown as { userAgentData?: UADataLowEntropy })?.userAgentData);
  if (uaData) {
    const significant = uaData.brands?.find(
      (b) => !/Not.?A.?Brand/i.test(b.brand)
    );
    if (significant) info.browser = `${significant.brand} ${significant.version}`;
    info.mobile = uaData.mobile;
    if (uaData.platform) info.os = uaData.platform;

    if (typeof uaData.getHighEntropyValues === 'function') {
      const high = await uaData
        .getHighEntropyValues(['platformVersion', 'architecture', 'bitness', 'model', 'uaFullVersion'])
        .catch(() => undefined);
      if (high) {
        const platformVersion = high.platformVersion as string | undefined;
        if (uaData.platform && platformVersion) {
          info.os = `${uaData.platform} ${platformVersion}`;
        }
        const arch = high.architecture as string | undefined;
        const bitness = high.bitness as string | undefined;
        if (arch) info.architecture = bitness ? `${arch} ${bitness}-bit` : arch;
        const model = high.model as string | undefined;
        if (model) info.deviceModel = model;
        const fullVersion = high.uaFullVersion as string | undefined;
        if (significant && fullVersion) info.browser = `${significant.brand} ${fullVersion}`;
      }
    }
  }
  info.browser ??= browserFromUserAgent(ua);
  info.os ??= osFromUserAgent(ua);
  info.engine = engineFromUserAgent(ua);

  // --- Hardware ---
  info.cpuCores = safe(() => nav?.hardwareConcurrency);
  info.deviceMemoryGb = safe(
    () => (nav as unknown as { deviceMemory?: number })?.deviceMemory
  );
  info.touchPoints = safe(() => nav?.maxTouchPoints);

  // --- Memory (Chromium-only performance.memory) ---
  const mem = safe(
    () => (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory
  );
  if (mem) {
    const mb = (bytes: number) => Math.round(bytes / (1024 * 1024));
    info.jsHeap = {
      usedMb: mb(mem.usedJSHeapSize),
      totalMb: mb(mem.totalJSHeapSize),
      limitMb: mb(mem.jsHeapSizeLimit),
    };
  }

  // --- Display ---
  if (typeof screen !== 'undefined') {
    const dpr = safe(() => window.devicePixelRatio);
    info.screen = `${screen.width}x${screen.height}${dpr && dpr !== 1 ? ` @${dpr}x` : ''}`;
  }
  if (typeof window !== 'undefined') {
    info.viewport = `${window.innerWidth}x${window.innerHeight}`;
  }

  // --- Locale / timezone ---
  info.language = nav?.language;
  info.languages = nav?.languages?.join(', ');
  info.timezone = safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone);

  // --- Network (Chromium-only) ---
  const conn = safe(
    () => (nav as unknown as { connection?: NetworkInformation })?.connection
  );
  if (conn) {
    const parts: string[] = [];
    if (conn.effectiveType) parts.push(conn.effectiveType);
    if (typeof conn.downlink === 'number') parts.push(`~${conn.downlink.toFixed(1)}Mbps`);
    if (typeof conn.rtt === 'number') parts.push(`rtt ${conn.rtt}ms`);
    if (conn.saveData) parts.push('saveData');
    if (parts.length) info.network = parts.join(' ');
  }

  // --- Misc ---
  info.online = safe(() => nav?.onLine);
  info.cookiesEnabled = safe(() => nav?.cookieEnabled);

  return info;
}
